import { callExternalApi } from './external-api.service';
import { createTask, storeTask, TASK_TYPES } from './task-manager.service';

const apiServerUrl = process.env.REACT_APP_API_SERVER_URL;

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

export async function listLighthouseParticipants(token, params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.append(k, v);
  });
  const { data, error } = await callExternalApi({
    config: {
      url: `${apiServerUrl}/api/admin/lighthouse/participants/?${qs}`,
      method: 'GET',
      headers: authHeaders(token),
    },
  });
  return { data, error };
}

/**
 * Fetch every page for the current filter set (Invite All / bulk actions).
 * Uses the API max page size (200) and walks until all rows are collected.
 */
export async function listAllLighthouseParticipants(token, params = {}) {
  const perPage = 200;
  const first = await listLighthouseParticipants(token, {
    ...params,
    page: 1,
    per_page: perPage,
  });
  if (first.error) return first;
  const total = Number(first.data?.total) || 0;
  const results = [...(first.data?.results || [])];
  let page = 2;
  while (results.length < total) {
    const next = await listLighthouseParticipants(token, {
      ...params,
      page,
      per_page: perPage,
    });
    if (next.error) return next;
    const batch = next.data?.results || [];
    if (!batch.length) break;
    results.push(...batch);
    page += 1;
    if (page > 500) break;
  }
  return {
    data: {
      ...(first.data || {}),
      results,
      total,
      page: 1,
      per_page: results.length,
    },
    error: null,
  };
}

export async function getLighthouseStats(token, params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.append(k, v);
  });
  const suffix = qs.toString() ? `?${qs}` : '';
  const { data, error } = await callExternalApi({
    config: {
      url: `${apiServerUrl}/api/admin/lighthouse/participants/stats/${suffix}`,
      method: 'GET',
      headers: authHeaders(token),
    },
  });
  return { data, error };
}

export async function getLighthouseCrcs(token) {
  const { data, error } = await callExternalApi({
    config: {
      url: `${apiServerUrl}/api/admin/lighthouse/participants/crcs/`,
      method: 'GET',
      headers: authHeaders(token),
    },
  });
  return { data, error };
}

export async function startLighthouseCampaign(token, slug, userIds) {
  const { data, error } = await callExternalApi({
    config: {
      url: `${apiServerUrl}/api/admin/lighthouse/campaigns/${slug}/start/`,
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      data: { user_ids: userIds },
    },
  });
  if (error) return { data: null, error };

  const taskId = data?.task_id;
  if (!taskId) {
    return { data: null, error: { message: 'Invite job did not return a task id' } };
  }

  const task = createTask(String(taskId), TASK_TYPES.LIGHTHOUSE_INVITE, {
    status: data?.status || 'queued',
    title: `Invite · ${userIds.length} member${userIds.length === 1 ? '' : 's'}`,
    message: 'Invite send queued',
    resultMode: 'json',
    autoRunOnSuccess: true,
    metadata: {
      taskId,
      userIds,
      slug,
    },
  });
  storeTask(task);

  return { data: { ...data, task }, error: null };
}

export async function getCampaignTemplate(token, { phase = 'CONSENT', step = 'initial' } = {}) {
  const qs = new URLSearchParams({ phase, step });
  const { data, error } = await callExternalApi({
    config: {
      url: `${apiServerUrl}/api/admin/lighthouse/campaigns/template/?${qs}`,
      method: 'GET',
      headers: authHeaders(token),
    },
  });
  return { data, error };
}

export async function getCampaignJob(token, taskId) {
  const { data, error } = await callExternalApi({
    config: {
      url: `${apiServerUrl}/api/admin/lighthouse/campaigns/jobs/${taskId}/`,
      method: 'GET',
      headers: authHeaders(token),
    },
  });
  return { data, error };
}

export async function previewPlutoConsent(token, userId) {
  const { data, error } = await callExternalApi({
    config: {
      url: `${apiServerUrl}/api/admin/lighthouse/participants/${userId}/pluto/consent/`,
      method: 'GET',
      headers: authHeaders(token),
    },
  });
  return { data, error };
}

export async function fetchPlutoConsentPdfBlob(token, userId) {
  try {
    const resp = await fetch(
      `${apiServerUrl}/api/admin/lighthouse/participants/${userId}/pluto/consent/pdf/`,
      { headers: authHeaders(token) }
    );
    if (!resp.ok) {
      let message = `Failed to load PDF (${resp.status})`;
      try {
        const body = await resp.json();
        if (body?.error) message = body.error;
      } catch {
        // ignore
      }
      return { blob: null, error: message };
    }
    const blob = await resp.blob();
    return { blob, error: null };
  } catch (e) {
    return { blob: null, error: e?.message || 'Failed to load PDF' };
  }
}

export async function previewPluto(token, userId) {
  const { data, error } = await callExternalApi({
    config: {
      url: `${apiServerUrl}/api/admin/lighthouse/participants/${userId}/pluto/preview/`,
      method: 'GET',
      headers: authHeaders(token),
    },
  });
  return { data, error };
}

export async function createPluto(token, userId, payload) {
  const { data, error } = await callExternalApi({
    config: {
      url: `${apiServerUrl}/api/admin/lighthouse/participants/${userId}/pluto/create/`,
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      data: { payload },
    },
  });
  return { data, error };
}

export async function linkPluto(token, userId, plutoId, compare = false) {
  const { data, error } = await callExternalApi({
    config: {
      url: `${apiServerUrl}/api/admin/lighthouse/participants/${userId}/pluto/link/`,
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      data: { pluto_id: plutoId, compare },
    },
  });
  return { data, error };
}

export async function previewSignIcf(token, userId) {
  const { data, error } = await callExternalApi({
    config: {
      url: `${apiServerUrl}/api/admin/lighthouse/participants/${userId}/sign-icf/preview/`,
      method: 'GET',
      headers: authHeaders(token),
    },
  });
  return { data, error };
}

export async function getSignIcfPiUrl(token, userId, documentId) {
  const { data, error } = await callExternalApi({
    config: {
      url: `${apiServerUrl}/api/admin/lighthouse/participants/${userId}/sign-icf/pi-url/`,
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      data: { document_id: documentId },
    },
  });
  return { data, error };
}

export async function completeSignIcf(token, userId) {
  const { data, error } = await callExternalApi({
    config: {
      url: `${apiServerUrl}/api/admin/lighthouse/participants/${userId}/sign-icf/complete/`,
      method: 'POST',
      headers: authHeaders(token),
    },
  });
  return { data, error };
}

export function lighthouseExportUrl(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.append(k, v);
  });
  return `${apiServerUrl}/api/admin/lighthouse/participants/export/?${qs}`;
}

export async function downloadLighthouseExport(token, params = {}) {
  const url = lighthouseExportUrl(params);
  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(token),
  });
  if (!response.ok) {
    const text = await response.text();
    let message = `Export failed (${response.status})`;
    try {
      const parsed = JSON.parse(text);
      if (parsed?.error) message = parsed.error;
    } catch {
      /* keep default */
    }
    return { error: { message } };
  }
  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = 'lighthouse-participants.csv';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(objectUrl);
  return { error: null };
}

export async function listMemberNotes(token, params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.append(k, v);
  });
  const { data, error } = await callExternalApi({
    config: {
      url: `${apiServerUrl}/api/admin/lighthouse/notes/?${qs}`,
      method: 'GET',
      headers: authHeaders(token),
    },
  });
  return { data, error };
}

export async function createMemberNote(token, payload) {
  const { data, error } = await callExternalApi({
    config: {
      url: `${apiServerUrl}/api/admin/lighthouse/notes/`,
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      data: payload,
    },
  });
  return { data, error };
}

export async function getMemberNote(token, noteId) {
  const { data, error } = await callExternalApi({
    config: {
      url: `${apiServerUrl}/api/admin/lighthouse/notes/${noteId}/`,
      method: 'GET',
      headers: authHeaders(token),
    },
  });
  return { data, error };
}

export async function determineScreenNote(token, noteId, payload) {
  const { data, error } = await callExternalApi({
    config: {
      url: `${apiServerUrl}/api/admin/lighthouse/notes/${noteId}/determine/`,
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      data: payload,
    },
  });
  return { data, error };
}

export async function resolveLighthouseChase(token, userId, state) {
  const { data, error } = await callExternalApi({
    config: {
      url: `${apiServerUrl}/api/admin/lighthouse/participants/${userId}/chase/resolve/`,
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      data: { state },
    },
  });
  return { data, error };
}

export async function listLighthouseCohortOptions(token, params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.append(k, v);
  });
  const suffix = qs.toString() ? `?${qs}` : '';
  const { data, error } = await callExternalApi({
    config: {
      url: `${apiServerUrl}/api/admin/lighthouse/cohort-options/${suffix}`,
      method: 'GET',
      headers: authHeaders(token),
    },
  });
  return { data, error };
}
